import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, TextField } from '@totalsoft/rocket-ui';
import { FormControlLabel, Checkbox, Grid } from '@mui/material';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Prompt } from 'react-router-dom';
import { ParamsContext } from 'providers/ParamsContext';
import FlexBox from 'components/common/FlexBox';

const SublimitHeaderComponent = ({ sublimit, index, dispatch, onChange, onSave, validation, facilityConfigurationSublimitsWrite }) => {
  const { cvsChAmount } = useContext(ParamsContext);
  const { t } = useTranslation();
  const onPropertyChanged = useCallback(
    property => propertyValue => {
      onChange(property, propertyValue, index);
    },
    [index, onChange]
  );

  const onSaveSublimitCallback = sublimitId => {
    dispatch({
      type: 'OnSublimitSaved',
      index: index,
      sublimitId
    });
  };

  const onSaveSublimit = () => {
    onSave('OnSaveSublimit', { sublimit, index }, onSaveSublimitCallback);
  };

  const onDeleteSublimitCallback = () => {
    dispatch({ type: 'OnSublimitDeleted', index });
  };

  const onDeleteSublimit = () => {
    if (window.confirm(t('Facility.Sublimits.Delete'))) {
      if (sublimit.sublimitId) onSave('OnDeleteSublimit', sublimit.sublimitId, onDeleteSublimitCallback);
      else onDeleteSublimitCallback();
    }
  };

  return (
    <Grid container spacing={2} alignItems='center'>
      <Prompt when={sublimit.changed || false} message={() => t('LeavingWithoutSavingFilter')} />
      <Grid item xs={6} sm={4}>
        <TextField
          fullWidth
          value={sublimit.sublimitCode || ''}
          onChange={onPropertyChanged('sublimitCode')}
          label={t('Facility.Sublimits.Code')}
          error={!isValid(validation.sublimitCode)}
          disabled={!facilityConfigurationSublimitsWrite}
          helperText={getErrors(validation.sublimitCode)}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField
          fullWidth
          value={sublimit.sublimitName || ''}
          onChange={onPropertyChanged('sublimitName')}
          label={t('Facility.Sublimits.Name')}
          error={!isValid(validation.sublimitName)}
          disabled={!facilityConfigurationSublimitsWrite}
          helperText={getErrors(validation.sublimitName)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          isNumeric
          inputProps={{ decimalScale: cvsChAmount }}
          value={sublimit.sublimitValue}
          onChange={onPropertyChanged('sublimitValue')}
          label={t('Facility.Sublimits.SublimitValue')}
          error={!isValid(validation.sublimitValue)}
          helperText={getErrors(validation.sublimitValue)}
          disabled={!facilityConfigurationSublimitsWrite}
        />
      </Grid>
      <Grid item xs={4} lg={2}>
        <TextField
          fullWidth
          isNumeric
          inputProps={{ decimalScale: cvsChAmount }}
          value={sublimit.availableAmount}
          label={t('Facility.Sublimits.AvailableAmount')}
          disabled={true}
        />
      </Grid>
      <Grid item xs={4} lg={2}>
        <TextField
          fullWidth
          isNumeric
          inputProps={{ decimalScale: cvsChAmount }}
          value={sublimit.blockedAmount}
          label={t('Facility.Sublimits.BlockedAmount')}
          disabled={true}
        />
      </Grid>
      <Grid item xs={4} lg={2}>
        <TextField
          fullWidth
          isNumeric
          inputProps={{ decimalScale: cvsChAmount }}
          value={sublimit.usedAmount}
          label={t('Facility.Sublimits.UsedAmount')}
          disabled={true}
        />
      </Grid>
      <Grid item flexGrow={1}>
        <FormControlLabel
          control={
            <Checkbox
              checked={sublimit.generalYN || false}
              onChange={onCheckBoxChange(onPropertyChanged('generalYN'))}
              color='primary'
              size='small'
              disabled={!facilityConfigurationSublimitsWrite}
            />
          }
          label={t('Facility.Sublimits.GeneralYN')}
        />
      </Grid>
      <Grid item>
        <FlexBox gap={0.5}>
          <IconButton
            onClick={onDeleteSublimit}
            color='primary'
            disabled={!facilityConfigurationSublimitsWrite}
            variant='text'
            type='delete'
            size='small'
            tooltip={t('Delete')}
          />
          <IconButton
            onClick={onSaveSublimit}
            color='primary'
            variant='text'
            type='save'
            size='small'
            tooltip={t('Save')}
            disabled={
              (validation |> getErrors) !== '' ||
              !(validation |> isValid) ||
              !sublimit.changed ||
              sublimit.saving ||
              !facilityConfigurationSublimitsWrite
            }
          />
        </FlexBox>
      </Grid>
    </Grid>
  );
};

SublimitHeaderComponent.propTypes = {
  sublimit: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  facilityConfigurationSublimitsWrite: PropTypes.bool
};

export default SublimitHeaderComponent;
